import { motion } from "motion/react";
import { panelClassName } from "@/lib/constants";
import {
  calculationMethodOptions,
  formatPrayerMethodSummary,
  getPrayerMethodLabel,
  methodPresets,
} from "@/lib/prayer-methods";
import type { PrayerLocation, PrayerMethodConfig, PrayerMethodValue, PrayerSnapshot } from "@/types/prayer";

type MethodPanelProps = {
  location: PrayerLocation | null;
  methodConfig: PrayerMethodConfig;
  onSelectMethod: (method: PrayerMethodValue) => void;
  snapshot: PrayerSnapshot | null;
};

const MethodPanel = ({ location, methodConfig, onSelectMethod, snapshot }: MethodPanelProps) => {
  const fajrTiming = snapshot?.timings.find((timing) => timing.event === "fajr") ?? null;
  const ishaTiming = snapshot?.timings.find((timing) => timing.event === "isha") ?? null;

  return (
    <motion.div
      animate={{ opacity: 1, x: 0 }}
      className={panelClassName}
      initial={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.4 }}
    >
      <div className="shrink-0 border-b border-zinc-800/50 px-5 pt-5 pb-4">
        <h2 className="text-lg font-semibold text-zinc-100">Calculation Method</h2>
        <p className="mt-1 text-xs text-zinc-500">
          Switch among Adhan presets and re-run the full curated globe in place.
        </p>
      </div>

      <div className="shrink-0 border-b border-zinc-800/50 px-5 py-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400">Active Preset</p>
            <p className="mt-2 text-sm font-semibold text-zinc-100">{getPrayerMethodLabel(methodConfig.method)}</p>
            <p className="mt-1 text-xs text-zinc-400">{formatPrayerMethodSummary(methodConfig)}</p>
          </div>

          <div className="rounded-2xl border border-zinc-800/60 bg-zinc-900/60 p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Live Preview</p>
            {!location || !snapshot ? (
              <p className="mt-2 text-sm text-zinc-400">Select a city to preview how Fajr and ʿIshāʾ move.</p>
            ) : (
              <>
                <p className="mt-2 text-sm font-semibold text-zinc-100">{location.city}</p>
                <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                  <div className="rounded-xl border border-zinc-800/70 bg-zinc-950/60 px-3 py-2">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500">Fajr</p>
                    <p className="mt-1 font-semibold text-sky-300">{fajrTiming?.time ?? "Pending"}</p>
                  </div>
                  <div className="rounded-xl border border-zinc-800/70 bg-zinc-950/60 px-3 py-2">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500">ʿIshāʾ</p>
                    <p className="mt-1 font-semibold text-violet-300">{ishaTiming?.time ?? "Pending"}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3">
        <div className="mb-3 rounded-2xl border border-zinc-800/60 bg-zinc-900/50 px-4 py-3">
          <p className="text-xs text-zinc-400">
            The selected method applies globally to every curated marker in this release, keeping the shell fast and
            consistent.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          {calculationMethodOptions.map((option) => {
            const preset = methodPresets[option.value];
            const isSelected = option.value === methodConfig.method;

            return (
              <button
                aria-pressed={isSelected}
                className={`w-full cursor-pointer rounded-2xl border px-4 py-3 text-left transition-colors ${
                  isSelected
                    ? "border-emerald-500/30 bg-emerald-500/5"
                    : "border-zinc-800/60 bg-zinc-900/40 hover:border-zinc-700 hover:bg-zinc-800/50"
                }`}
                key={option.value}
                onClick={() => onSelectMethod(option.value)}
                type="button"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className={`text-sm font-semibold ${isSelected ? "text-zinc-100" : "text-zinc-200"}`}>
                      {option.label}
                    </p>
                    <p className="mt-1 text-xs text-zinc-500">{formatPrayerMethodSummary(preset)}</p>
                  </div>
                  <span
                    className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                      isSelected
                        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                        : "border-zinc-700 bg-zinc-900 text-zinc-500"
                    }`}
                  >
                    {isSelected ? "Selected" : "Preset"}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

export default MethodPanel;
